import React, { useState, useEffect } from 'react';
import './Login.css';
import PasswordResetRequest from './PasswordResetRequest';

interface LoginProps {
  onLogin: (token: string, user: any) => void;
  successMessage?: string;
}

interface Worker {
  ID: number;
  Name: string;
}

const Login: React.FC<LoginProps> = ({ onLogin, successMessage }) => {
  // Login mode: worker (default) or admin
  const [loginMode, setLoginMode] = useState<'worker' | 'admin'>('worker');

  // Worker login state
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>('');
  const [workerPassword, setWorkerPassword] = useState('');

  // Admin login state
  const [username, setUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  // Shared state
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const basePath = '/databaseOps/';

  // Fetch worker list for dropdown
  useEffect(() => {
    if (loginMode === 'worker') {
      fetch(`${basePath}getWorkerList.php`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            setWorkers(data);
          }
        })
        .catch(err => console.error('Failed to load worker list:', err));
    }
  }, [loginMode]);

  const handleWorkerLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${basePath}workerLogin.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          worker_id: parseInt(selectedWorkerId),
          password: workerPassword
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Anmeldung fehlgeschlagen');
        setIsLoading(false);
        return;
      }

      if (data.success && data.token) {
        onLogin(data.token, data.user);
      } else {
        setError('Ungültige Antwort vom Server');
        setIsLoading(false);
      }
    } catch (err) {
      setError('Netzwerkfehler. Bitte erneut versuchen.');
      setIsLoading(false);
    }
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(`${basePath}login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: adminPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Login failed');
        setIsLoading(false);
        return;
      }

      if (data.success && data.token) {
        onLogin(data.token, data.user);
      } else {
        setError('Invalid response from server');
        setIsLoading(false);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setIsLoading(false);
    }
  };

  if (showPasswordReset) {
    return <PasswordResetRequest onBack={() => setShowPasswordReset(false)} />;
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>Reitstall Manager</h1>
          <span className="login-version">v{process.env.REACT_APP_VERSION || '2.0.0'}</span>
        </div>

        {loginMode === 'worker' ? (
          <>
            <h2>Mitarbeiter-Anmeldung</h2>

            {successMessage && <div className="login-success">{successMessage}</div>}
            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleWorkerLogin}>
              <div className="form-group">
                <label htmlFor="worker-select">Name</label>
                <select
                  id="worker-select"
                  value={selectedWorkerId}
                  onChange={(e) => setSelectedWorkerId(e.target.value)}
                  required
                  disabled={isLoading}
                  autoFocus
                >
                  <option value="">-- Bitte auswählen --</option>
                  {workers.map(w => (
                    <option key={w.ID} value={w.ID}>{w.Name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="worker-password">Passwort</label>
                <input
                  id="worker-password"
                  type="password"
                  value={workerPassword}
                  onChange={(e) => setWorkerPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <button type="submit" disabled={isLoading || !selectedWorkerId}>
                {isLoading ? 'Anmelden...' : 'Anmelden'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <button
                type="button"
                onClick={() => { setLoginMode('admin'); setError(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#00695c',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Admin-Anmeldung
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Admin-Anmeldung</h2>

            {error && <div className="login-error">{error}</div>}

            <form onSubmit={handleAdminLogin}>
              <div className="form-group">
                <label htmlFor="username">Benutzername</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="admin-password">Passwort</label>
                <input
                  id="admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Anmelden...' : 'Anmelden'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
              <button
                type="button"
                onClick={() => setShowPasswordReset(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#00695c',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px',
                  marginRight: '15px'
                }}
              >
                Passwort vergessen?
              </button>
              <button
                type="button"
                onClick={() => { setLoginMode('worker'); setError(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#00695c',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px'
                }}
              >
                Mitarbeiter-Anmeldung
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
